import { useState, useEffect, useCallback } from 'react'
import { Timestamp } from 'firebase/firestore'
import {
  UserPOI,
  CreateUserPOIData,
  UpdateUserPOIData
} from '../types/userPois'
import {
  getUserPOIsForRoute,
  createUserPOI,
  updateUserPOI,
  deleteUserPOI,
  confirmUserPOI,
  markUserPOIOutdated,
  removeUserPOIVote
} from '../services/firebase/userPois'

interface UseUserPOIsProps {
  routeId: string
  userId?: string
  userDisplayName?: string
  userPhotoURL?: string
}

export function useUserPOIs({
  routeId,
  userId,
  userDisplayName,
  userPhotoURL
}: UseUserPOIsProps) {
  const [userPOIs, setUserPOIs] = useState<UserPOI[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load user POIs
  const loadUserPOIs = useCallback(async () => {
    if (!routeId) return

    setLoading(true)
    setError(null)

    try {
      const pois = await getUserPOIsForRoute(routeId)
      console.log('📍 Loaded user POIs:', pois.length)
      setUserPOIs(pois)
    } catch (err) {
      console.error('Error loading user POIs:', err)
      setError('Failed to load user POIs')
    } finally {
      setLoading(false)
    }
  }, [routeId])

  // Load POIs on mount and route change
  useEffect(() => {
    loadUserPOIs()
  }, [loadUserPOIs])

  // Add user POI
  const addUserPOI = useCallback(async (poiData: CreateUserPOIData): Promise<string> => {
    if (!userId || !userDisplayName) {
      throw new Error('User must be authenticated to add POIs')
    }

    try {
      const poiId = await createUserPOI(
        poiData,
        userId,
        userDisplayName,
        userPhotoURL
      )

      // Reload POIs to show the new one
      await loadUserPOIs()
      
      return poiId
    } catch (err) {
      console.error('Error adding user POI:', err)
      throw new Error('Failed to add POI')
    }
  }, [userId, userDisplayName, userPhotoURL, loadUserPOIs])

  // Update user POI
  const editUserPOI = useCallback(async (poiId: string, updates: UpdateUserPOIData): Promise<void> => {
    try {
      await updateUserPOI(poiId, updates)
      
      // Update local state optimistically
      setUserPOIs(prev => prev.map(poi =>
        poi.id === poiId
          ? { ...poi, ...updates, updatedAt: new Date() as any }
          : poi
      ))
    } catch (err) {
      console.error('Error updating user POI:', err)
      throw new Error('Failed to update POI')
    }
  }, [])

  // Delete user POI
  const removeUserPOI = useCallback(async (poiId: string): Promise<void> => {
    try {
      await deleteUserPOI(poiId)
      
      // Update local state optimistically
      setUserPOIs(prev => prev.filter(poi => poi.id !== poiId))
    } catch (err) {
      console.error('Error deleting user POI:', err)
      throw new Error('Failed to delete POI')
    }
  }, [])

  // Confirm POI accuracy
  const confirmPOI = useCallback(async (poiId: string): Promise<void> => {
    if (!userId) return

    try {
      await confirmUserPOI(poiId, userId, userDisplayName, userPhotoURL)
      
      // Update local state optimistically
      setUserPOIs(prev => prev.map(poi => {
        if (poi.id === poiId) {
          const newConfirmations = { ...poi.confirmations }
          const newOutdatedVotes = { ...poi.outdatedVotes }
          
          // Add confirmation with current timestamp and user info
          newConfirmations[userId] = {
            timestamp: Timestamp.now(),
            userName: userDisplayName,
            userPhoto: userPhotoURL
          }
          // Remove from outdated votes
          delete newOutdatedVotes[userId]
          
          return {
            ...poi,
            confirmations: newConfirmations,
            outdatedVotes: newOutdatedVotes
          }
        }
        return poi
      }))
    } catch (err) {
      console.error('Error confirming POI:', err)
    }
  }, [userId, userDisplayName, userPhotoURL])

  // Mark POI as outdated
  const markOutdated = useCallback(async (poiId: string): Promise<void> => {
    if (!userId) return

    try {
      await markUserPOIOutdated(poiId, userId, userDisplayName, userPhotoURL)
      
      // Update local state optimistically
      setUserPOIs(prev => prev.map(poi => {
        if (poi.id === poiId) {
          const newConfirmations = { ...poi.confirmations }
          const newOutdatedVotes = { ...poi.outdatedVotes }
          
          // Add outdated vote with current timestamp and user info
          newOutdatedVotes[userId] = {
            timestamp: Timestamp.now(),
            userName: userDisplayName,
            userPhoto: userPhotoURL
          }
          // Remove from confirmations
          delete newConfirmations[userId]
          
          return {
            ...poi,
            confirmations: newConfirmations,
            outdatedVotes: newOutdatedVotes
          }
        }
        return poi
      }))
    } catch (err) {
      console.error('Error marking POI as outdated:', err)
    }
  }, [userId, userDisplayName, userPhotoURL])

  // Remove vote
  const removeVote = useCallback(async (poiId: string): Promise<void> => {
    if (!userId) return

    try {
      await removeUserPOIVote(poiId, userId)
      
      // Update local state optimistically
      setUserPOIs(prev => prev.map(poi => {
        if (poi.id === poiId) {
          const newConfirmations = { ...poi.confirmations }
          const newOutdatedVotes = { ...poi.outdatedVotes }
          
          // Remove from both
          delete newConfirmations[userId]
          delete newOutdatedVotes[userId]
          
          return {
            ...poi,
            confirmations: newConfirmations,
            outdatedVotes: newOutdatedVotes
          }
        }
        return poi
      }))
    } catch (err) {
      console.error('Error removing vote:', err)
    }
  }, [userId])

  return {
    userPOIs,
    loading,
    error,
    addUserPOI,
    editUserPOI,
    removeUserPOI,
    confirmPOI,
    markOutdated,
    removeVote,
    refreshPOIs: loadUserPOIs
  }
}